import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxfAccountingV1ManualEntryQueryServiceManualEntryGet } from '@interxfi/api-client/generated/hooks/ixf-accounting-v1-manual-entry-query-service-manual-entry-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxfAccountingV1ManualEntryState } from '@interxfi/types/generated/api';
import { AccountingManualEntryDeltasTable } from '../../../../../../../../../features/accounting/manual-entry/accounting-manual-entry-deltas-table';
import { AccountingManualEntryEntityMenu } from '../../../../../../../../../features/accounting/manual-entry/accounting-manual-entry-entity-menu';
import { AccountingManualEntryEventSummary } from '../../../../../../../../../features/accounting/manual-entry/accounting-manual-entry-event';
import { getAccountingManualEntryStateListItems } from '../../../../../../../../../features/accounting/manual-entry/accounting-manual-entry-list-items';
import { getAccountingManualEntryPath } from '../../../../../../../../../features/accounting/manual-entry/accounting-manual-entry-routing';
import { AccountingManualEntryStatusPill } from '../../../../../../../../../features/accounting/manual-entry/accounting-manual-entry-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/finance/_finance/journal/_manual-entry/$entryId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `manual-entry-${params.entryId}`,
        label: 'Manual Entry',
        to: getAccountingManualEntryPath({ kind: 'detail', keys: { entryId: params.entryId } }),
        suffix: <CopyButton content={params.entryId} label="Copy Entry ID" tooltip={{ copy: params.entryId, copied: 'Copied Entry ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Manual Entry'),
  component: AccountingManualEntryDetail,
});

function getListItems(data: IxfAccountingV1ManualEntryState | undefined) {
  const items = getAccountingManualEntryStateListItems(data);

  return {
    left: [
      items.data.date,
      items.data.description,
      items.data.postedTimestamp,
      ...(items.data.autoReverseDate ? [items.data.autoReverseDate] : []),
    ] as const satisfies ReadonlyListItem[],
    right: [
      items.metadata.createdAt,
      items.metadata.updatedAt,
      items.entryId,
      items.data.transactionId,
      ...(items.data.reversingTransactionId ? [items.data.reversingTransactionId] : []),
    ] as const satisfies ReadonlyListItem[],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load entry', 'view this manual entry');

function AccountingManualEntryDetail() {
  const { entryId } = Route.useParams();
  const request = { entryId } as const;
  const { data, error, isPending } = useIxfAccountingV1ManualEntryQueryServiceManualEntryGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.manualEntry), [data?.manualEntry]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
            <AccountingManualEntryStatusPill isPending={showPending} size="large" status={data?.manualEntry?.status} />
          </HorizontalStack>

          <AccountingManualEntryEntityMenu entryId={entryId} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <AccountingManualEntryEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3 lineHeight="1rem">Deltas</Typography.Level3>

          <AccountingManualEntryDeltasTable deltas={data?.manualEntry?.data?.deltas || []} showSkeleton={showPending} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
